import { RefreshCw } from 'lucide-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useCommerceOutcomeSeries, type CommerceOutcomePoint } from '@/hooks/useAdminAnalytics';

const SOLD = 'hsl(142, 71%, 45%)';
const PENDING = 'hsl(38, 92%, 50%)';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

const NOTE =
    'Por día de creación. Vendidas: pagadas (siguen en curso o ya entregadas). Pendientes: sin pagar. No incluye canceladas, fallidas ni reembolsadas.';

interface CommerceOutcomeChartsProps {
    days: number | null;
    isMobile?: boolean;
}

function formatDayLabel(val: string, withYear = false): string {
    const [year, month, day] = val.slice(0, 10).split('-');
    if (!year || !month || !day) return val;
    return withYear ? `${Number(day)}/${Number(month)}/${year}` : `${Number(day)}/${Number(month)}`;
}

function sum(rows: CommerceOutcomePoint[], key: 'sold' | 'pending', kind: 'license' | 'store'): number {
    const field = kind === 'license'
        ? (key === 'sold' ? 'licenseSold' : 'licensePending')
        : (key === 'sold' ? 'storeSold' : 'storePending');
    return rows.reduce((acc, row) => acc + row[field], 0);
}

function toChartRows(rows: CommerceOutcomePoint[], kind: 'license' | 'store') {
    return rows.map((row) => ({
        date: row.date,
        sold: kind === 'license' ? row.licenseSold : row.storeSold,
        pending: kind === 'license' ? row.licensePending : row.storePending,
    }));
}

function OutcomeChart({
    title,
    rows,
    kind,
    isMobile,
}: {
    title: string;
    rows: CommerceOutcomePoint[];
    kind: 'license' | 'store';
    isMobile: boolean;
}) {
    const sold = sum(rows, 'sold', kind);
    const pending = sum(rows, 'pending', kind);
    const chartData = toChartRows(rows, kind);

    return (
        <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0">
            <div className="flex items-baseline justify-between gap-3 mb-1">
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {sold.toLocaleString('es-ES')} vendidas · {pending.toLocaleString('es-ES')} pendientes
                </p>
            </div>
            <div className={isMobile ? 'h-[220px]' : 'h-[260px]'}>
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 8,
                                right: isMobile ? 4 : 8,
                                bottom: isMobile ? 8 : 4,
                                left: isMobile ? 0 : -10,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                tickLine={false}
                                axisLine={false}
                                minTickGap={isMobile ? 36 : 24}
                                interval="preserveStartEnd"
                                tickFormatter={(val: string) => formatDayLabel(String(val ?? ''))}
                            />
                            <YAxis
                                width={isMobile ? 28 : 36}
                                allowDecimals={false}
                                tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                contentStyle={tooltipStyle}
                                labelStyle={{ color: 'hsl(210 40% 98%)' }}
                                labelFormatter={(val) => formatDayLabel(String(val ?? ''), true)}
                                formatter={(value: number, name: string) => [value.toLocaleString('es-ES'), name]}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="sold" name="Vendidas" stackId="outcome" fill={SOLD} />
                            <Bar dataKey="pending" name="Pendientes" stackId="outcome" fill={PENDING} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                        No hay datos para el rango seleccionado
                    </div>
                )}
            </div>
        </div>
    );
}

export function CommerceOutcomeCharts({ days, isMobile = false }: CommerceOutcomeChartsProps) {
    const { data, isLoading, error } = useCommerceOutcomeSeries(days);

    if (isLoading) {
        return (
            <div className="rounded-xl border border-border bg-card p-6 flex items-center justify-center text-muted-foreground">
                <RefreshCw size={20} className="animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <p className="text-sm text-rose-500">
                {(error as Error).message || 'No se pudo cargar licencias y pedidos.'}
            </p>
        );
    }

    const rows = data ?? [];

    return (
        <div className="space-y-3 min-w-0">
            <p className="text-[11px] text-muted-foreground">{NOTE}</p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 min-w-0">
                <OutcomeChart title="Licencias" rows={rows} kind="license" isMobile={isMobile} />
                <OutcomeChart title="Pedidos de tienda" rows={rows} kind="store" isMobile={isMobile} />
            </div>
        </div>
    );
}
